import { useState, useEffect, useRef, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Heart, Search as SearchIcon, X, CreditCard, Banknote, Upload, Check, User, Mic, MapPin, Calendar, FileText, Image as ImageIcon, Video, Info, Ticket, Users as UsersIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { usePaystackPayment } from 'paystack-react';
import '@/App.css';
import success_1 from '@/assets/success_1.jpeg';
import logo from '@/assets/logo.jpeg';
import success_3 from '@/assets/success_3.jpeg';
import highlight_1 from '@/assets/highlight_1.jpeg';
import Navigation from '@/components/nav';
import FooterSection from '@/components/footer';
import { useParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE || "https://qwaqwatalentshow.co.za";

interface Participant {
    id: number;
    event_id: number;

    // Identity
    stage_name: string;
    full_name?: string | null;

    // Talent info
    talent: string;
    competition?: string | null;

    // Individual or group
    is_individual: boolean;

    // Content
    description?: string | null;
    image?: string | null;
    video_url?: string | null;

    // Voting / monetization
    votes: number;
    price: number;

    // Extra fields
    status: 'active' | 'inactive' | 'disqualified';
    position?: number | null;
    age?: number | null;
    location?: string | null;

    // Timestamps
    created_at: string; // or Date if you parse it
    updated_at: string; // or Date
}

interface EventDetails {
    venue_details: string | null;
    vip_lounge: string | null;
    dress_code: string | null;
    age_requirement: string | null;
    entry_fee: string | null;
    highlights: string | null;
    price_per_vote: number;
}

interface EventData {
    id: number;
    title: string;
    description: string;
    close_date: string;
    close_time: string;
    registration_enabled: number;
    start_date: string;
    start_time: string;
    category: string;
    featured: boolean;
    status: string;
    created_at: string;
    updated_at: string;
    youtube_live_url: string | null;
    youtube_live_id: string | null;
    datetime_iso: string | null;
    price: number;
    images: string[] | null;
    primary_image: string | null;
    artists: string[] | null;
    details: EventDetails;
    votes_enabled: number;
}



interface ParticipantDetailPopupProps {
    participant: Participant | null;
    totalVotes: number;
    onClose: () => void;
    onPaymentSubmit: (paymentMethod: string) => void;
    price: number;
    eventid: number;
}

function ParticipantDetailPopup({ participant, totalVotes, onClose, onPaymentSubmit, price, eventid }: ParticipantDetailPopupProps) {
    const [numberOfVotes, setNumberOfVotes] = useState(1);
    const [paymentMethod, setPaymentMethod] = useState<'online' | 'offline'>('online');

    const [email, setEmail] = useState('');

    if (!participant) return null;

    const totalPrice = price * numberOfVotes;

    const config = {
        reference: (new Date()).getTime().toString(),
        email: email || "user@example.com",
        amount: totalPrice * 100, // Paystack expects kobo
        publicKey: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || 'pk_test_xxx',
    };

    const onSuccess = (reference: any) => {
        console.log(reference);
        onPaymentSubmit('online');
    };

    const onClosePaystack = () => {
        console.log('closed');
    };

    const paystackInitialize = usePaystackPayment(config);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        // Simulate payment processing
        await new Promise(resolve => setTimeout(resolve, 1000));
        onPaymentSubmit(paymentMethod);
        setIsSubmitting(false);
    };

    const increaseVotes = () => setNumberOfVotes(prev => prev + 1);
    const decreaseVotes = () => setNumberOfVotes(prev => (prev > 1 ? prev - 1 : 1));

    return createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 9999999999 }}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
                {/* HEADER */}
                <div className="flex-shrink-0 flex items-center justify-between bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold p-6 text-white">
                    <h2 className="text-2xl font-black">Participant Details</h2>
                    <button onClick={onClose} className="hover:bg-white/20 p-2 rounded-full transition">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* PARTICIPANT INFO */}
                    <div className="flex gap-4">

                        <img
                            src={
                                participant.image
                                    ? `${API_BASE}${participant.image}`
                                    : success_1
                            }
                            alt={participant.stage_name}
                            className="w-24 h-24 rounded-2xl object-cover border-2 border-QWA2_TALENT_SHOW-gold"
                        />
                        <div className="flex-1">
                            <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-black">{participant.stage_name}</h3>
                            <p className="text-QWA2_TALENT_SHOW-gray text-sm mt-1">{participant.talent}</p>
                            <span className="inline-block mt-2 bg-QWA2_TALENT_SHOW-gold/20 text-QWA2_TALENT_SHOW-gold px-3 py-1 rounded-full text-xs font-semibold">
                                {participant.competition}
                            </span>
                        </div>
                    </div>

                    {/* STATS */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-gradient-to-br from-QWA2_TALENT_SHOW-gold/10 to-transparent rounded-2xl p-4 text-center border border-QWA2_TALENT_SHOW-gold/20">
                            <p className="text-xs uppercase tracking-wider text-QWA2_TALENT_SHOW-gray mb-1">Current Votes</p>
                            <p className="text-3xl font-black text-QWA2_TALENT_SHOW-black">{totalVotes.toLocaleString()}</p>
                        </div>
                        <div className="bg-gradient-to-br from-QWA2_TALENT_SHOW-red/10 to-transparent rounded-2xl p-4 text-center border border-QWA2_TALENT_SHOW-red/20">
                            <p className="text-xs uppercase tracking-wider text-QWA2_TALENT_SHOW-gray mb-1">Price Per Vote</p>
                            <p className="text-3xl font-black text-QWA2_TALENT_SHOW-black">R{price}</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-500/10 to-transparent rounded-2xl p-4 text-center border border-green-500/20">
                            <p className="text-xs uppercase tracking-wider text-QWA2_TALENT_SHOW-gray mb-1">Status</p>
                            <p className="text-3xl font-black text-green-600">✓</p>
                        </div>
                    </div>

                    {/* NUMBER OF VOTES SELECTOR */}
                    <div className="space-y-3">
                        <h4 className="text-lg font-bold text-QWA2_TALENT_SHOW-black">Number of Votes</h4>
                        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full">

                            {/* DECREASE */}
                            <button
                                onClick={decreaseVotes}
                                className="w-full sm:w-12 h-12 flex items-center justify-center rounded-xl sm:rounded-full 
    bg-QWA2_TALENT_SHOW-red/10 hover:bg-QWA2_TALENT_SHOW-red/20 
    text-QWA2_TALENT_SHOW-red font-bold text-2xl transition active:scale-95"
                            >
                                −
                            </button>

                            {/* INPUT */}
                            <input
                                type="number"
                                value={numberOfVotes}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value) || 1;
                                    if (val > 0) setNumberOfVotes(val);
                                }}
                                className="w-full sm:flex-1 text-center text-2xl sm:text-3xl font-black 
                                text-QWA2_TALENT_SHOW-black border-2 border-QWA2_TALENT_SHOW-gold/30 
                                rounded-xl sm:rounded-2xl py-3 sm:py-2 
                                focus:border-QWA2_TALENT_SHOW-gold outline-none transition"
                            />

                            {/* INCREASE */}
                            <button
                                onClick={increaseVotes}
                                className="w-full sm:w-12 h-12 flex items-center justify-center rounded-xl sm:rounded-full 
                                bg-QWA2_TALENT_SHOW-gold/10 hover:bg-QWA2_TALENT_SHOW-gold/20 
                                text-QWA2_TALENT_SHOW-gold font-bold text-2xl transition active:scale-95"
                            >
                                +
                            </button>

                        </div>
                    </div>

                    {/* TOTAL PRICE DISPLAY */}
                    <div className="bg-gradient-to-r from-QWA2_TALENT_SHOW-gold/20 to-QWA2_TALENT_SHOW-red/20 rounded-2xl p-6 border-2 border-QWA2_TALENT_SHOW-gold/30">
                        <div className="text-center">
                            <p className="text-sm uppercase tracking-wider text-QWA2_TALENT_SHOW-gray mb-2">Total Amount Due</p>
                            <p className="text-4xl md:text-5xl font-black text-QWA2_TALENT_SHOW-black">
                                R{totalPrice.toLocaleString()}
                            </p>
                            <p className="text-xs text-QWA2_TALENT_SHOW-gray mt-2">
                                {numberOfVotes} vote{numberOfVotes !== 1 ? 's' : ''} × R{price} = R{totalPrice}
                            </p>
                        </div>
                    </div>

                    {/* PAYMENT METHOD */}
                    <div className="space-y-3">
                        <h4 className="text-lg font-bold text-QWA2_TALENT_SHOW-black">Payment Method</h4>
                        <div className="space-y-3">
                            <label className="flex items-center gap-3 p-4 border-2 rounded-2xl cursor-pointer transition"
                                style={{ borderColor: paymentMethod === 'online' ? '#D4AF37' : '#E5E7EB' }}>
                                <input
                                    type="radio"
                                    name="payment"
                                    value="online"
                                    checked={paymentMethod === 'online'}
                                    onChange={(e) => setPaymentMethod(e.target.value as 'online' | 'offline')}
                                    className="w-5 h-5 accent-QWA2_TALENT_SHOW-gold cursor-pointer"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-QWA2_TALENT_SHOW-gold" />
                                        <p className="font-semibold text-QWA2_TALENT_SHOW-black">Online Payment</p>
                                    </div>
                                    <p className="text-xs text-QWA2_TALENT_SHOW-gray mt-1">Card, Bank Transfer, E-wallet</p>
                                </div>
                            </label>

                            <label className="flex items-center gap-3 p-4 border-2 rounded-2xl cursor-pointer transition"
                                style={{ borderColor: paymentMethod === 'offline' ? '#D4AF37' : '#E5E7EB' }}>
                                <input
                                    type="radio"
                                    name="payment"
                                    value="offline"
                                    checked={paymentMethod === 'offline'}
                                    onChange={(e) => setPaymentMethod(e.target.value as 'online' | 'offline')}
                                    className="w-5 h-5 accent-QWA2_TALENT_SHOW-gold cursor-pointer"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <Banknote className="w-5 h-5 text-QWA2_TALENT_SHOW-gold" />
                                        <p className="font-semibold text-QWA2_TALENT_SHOW-black">Bank Transfer / POP</p>
                                    </div>
                                    <p className="text-xs text-QWA2_TALENT_SHOW-gray mt-1">Direct Bank Deposit or EFT</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* EMAIL FOR ONLINE PAYMENT */}
                    {paymentMethod === 'online' && (
                        <div className="space-y-3">
                            <h4 className="text-lg font-bold text-QWA2_TALENT_SHOW-black">Email Address</h4>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                required
                                className="w-full border-2 border-QWA2_TALENT_SHOW-gold/30 focus:border-QWA2_TALENT_SHOW-gold rounded-2xl py-3"
                            />
                        </div>
                    )}

                    {/* POP RECEIPT UPLOAD - Show only when offline is selected */}
                    {paymentMethod === 'offline' && (
                        <div className="space-y-3 bg-QWA2_TALENT_SHOW-gold/10 rounded-2xl p-4 border-2 border-QWA2_TALENT_SHOW-gold/30 text-QWA2_TALENT_SHOW-black animate-in fade-in zoom-in duration-300">
                            <h4 className="text-lg font-bold">Banking Details</h4>
                            <div className="grid grid-cols-2 gap-y-2 text-sm">
                                <span className="font-semibold text-QWA2_TALENT_SHOW-gray">Bank:</span>
                                <span className="font-medium text-right">Capitec Bank</span>

                                <span className="font-semibold text-QWA2_TALENT_SHOW-gray">Account Name:</span>
                                <span className="font-medium text-right">Qwaqwa Talent Show</span>

                                <span className="font-semibold text-QWA2_TALENT_SHOW-gray">Account No.:</span>
                                <span className="font-medium text-right text-QWA2_TALENT_SHOW-gold">2528410898</span>

                                <span className="font-semibold text-QWA2_TALENT_SHOW-gray">Branch Code:</span>
                                <span className="font-medium text-right">470010</span>

                                <span className="font-semibold text-QWA2_TALENT_SHOW-gray">Reference:</span>
                                <span className="font-medium text-right text-xs"><b>Q2TSE{eventid}P{participant.id}</b></span>
                            </div>
                            <p className="text-[10px] text-QWA2_TALENT_SHOW-gray mt-4 text-center border-t border-QWA2_TALENT_SHOW-gold/20 pt-3">
                                With correct Reference the vote will be updated within 24-48 hours after payment confirmation. For any issues please contact us at <a href="mailto:info@qwaqwatalentshow.com" className="text-QWA2_TALENT_SHOW-gold hover:underline">info@qwaqwatalentshow.com</a>
                            </p>
                        </div>
                    )}

                    {/* SUBMIT BUTTON */}
                    {paymentMethod === 'online' ? (
                        <Button
                            onClick={() => paystackInitialize(onSuccess, onClosePaystack)}
                            disabled={!email}
                            className="w-full bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white font-bold uppercase tracking-wider py-3 rounded-2xl hover:opacity-90 transition disabled:opacity-50"
                        >
                            Pay with Paystack - R{totalPrice.toLocaleString()}
                        </Button>
                    ) : null}
                    {paymentMethod === 'online' && !email && (
                        <p className="text-xs text-QWA2_TALENT_SHOW-red text-center">⚠ Please enter your email to continue</p>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}


function ParticipantsSection({ id, participants, totalParticipants, loading, price, votes_enabled }: { id?: string; participants: Participant[]; totalParticipants: number; loading: boolean; price: number; votes_enabled: number }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [userVotes, setUserVotes] = useState<Record<number, number>>({});
    const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);

    const maxVotes = participants.length > 0 ? Math.max(...participants.map(p => p.votes + (userVotes[p.id] || 0))) : 1;

    const filteredParticipants = participants.filter(p =>
        p.stage_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.competition && p.competition.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleVote = (id: number) => {
        const participant = participants.find(p => p.id === id);
        if (participant) {
            setSelectedParticipant(participant);
        }
    };

    const handlePaymentSubmit = (paymentMethod: string) => {
        if (selectedParticipant) {
            setUserVotes(prev => ({
                ...prev,
                [selectedParticipant.id]: (prev[selectedParticipant.id] || 0) + 1
            }));
            // Show success message
            alert(`✓ Support submitted via ${paymentMethod.toUpperCase()}!\nThank you for supporting ${selectedParticipant.stage_name}`);
            setSelectedParticipant(null);
        }
    };

    if (loading) {
        return (
            <div className="space-y-8">
                <div className="text-center py-12">
                    <p className="text-QWA2_TALENT_SHOW-gray text-lg">Loading participants...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h2 className="text-3xl md:text-5xl font-black text-QWA2_TALENT_SHOW-black mb-4">
                    Meet the Participants
                </h2>
                <p className="text-QWA2_TALENT_SHOW-gray text-base md:text-lg max-w-2xl mx-auto">
                    Browse all participants, search by name or competition, and cast your votes
                </p>
            </div>

            <div className="relative max-w-md mx-auto mb-8">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-QWA2_TALENT_SHOW-gray" />
                <Input
                    placeholder="Search participants..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 bg-white border-2 border-QWA2_TALENT_SHOW-gold/30 focus:border-QWA2_TALENT_SHOW-gold rounded-3xl py-3"
                />
            </div>

            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {filteredParticipants.map((participant, index) => {
                    const totalVotes = participant.votes + (userVotes[participant.id] || 0);
                    const percentage = (totalVotes / maxVotes) * 100;

                    return (
                        <div
                            key={participant.id}
                            className="group relative overflow-hidden rounded-[28px] border border-white/10 bg-[#111111] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.35)] transition-all duration-500 hover:-translate-y-1"
                        >

                            {/* Glow */}
                            <div className="absolute -top-20 right-0 w-40 h-40 bg-QWA2_TALENT_SHOW-gold/10 blur-3xl rounded-full" />
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-QWA2_TALENT_SHOW-red/10 blur-3xl rounded-full" />

                            {/* CONTENT */}
                            <div className="relative z-10 flex flex-col gap-5">

                                {/* TOP */}
                                <div className="flex items-start gap-4">

                                    {/* RANK */}
                                    <div className="relative">

                                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold flex items-center justify-center shadow-lg">
                                            <span className="text-sm font-black text-white">
                                                #{index + 1}
                                            </span>
                                        </div>

                                    </div>

                                    {/* IMAGE */}
                                    <div className="relative">

                                        <img
                                            src={
                                                participant.image
                                                    ? `${API_BASE}${participant.image}`
                                                    : success_1
                                            }
                                            alt={participant.stage_name}
                                            className="w-16 h-16 rounded-2xl object-cover border border-white/10"
                                        />

                                        {/* Online Dot */}
                                        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-400 border-2 border-[#111111]" />

                                    </div>

                                    {/* INFO */}
                                    <div className="flex-1 min-w-0">

                                        <p className="text-[10px] uppercase tracking-[3px] text-white/40 mb-1">
                                            {participant.competition}
                                        </p>

                                        <h3 className="text-lg font-black text-white truncate">
                                            {participant.stage_name}
                                        </h3>

                                        <div className="flex items-center gap-2 mt-2">

                                            <div className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[2px] text-white/60">
                                                Contestant
                                            </div>

                                            {votes_enabled === 1 && (
                                                <div className="px-2 py-1 rounded-full bg-QWA2_TALENT_SHOW-gold/10 border border-QWA2_TALENT_SHOW-gold/20 text-[10px] uppercase tracking-[2px] text-QWA2_TALENT_SHOW-gold">
                                                    Active
                                                </div>
                                            )}

                                        </div>

                                    </div>

                                    {/* VOTES */}
                                    {votes_enabled === 1 && (
                                        <div className="text-right">

                                            <h2 className="text-2xl font-black text-white leading-none">
                                                {totalVotes.toLocaleString()}
                                            </h2>

                                            <p className="text-[10px] uppercase tracking-[3px] text-white/40 mt-1">
                                                Votes
                                            </p>

                                        </div>
                                    )}

                                </div>

                                {/* PROGRESS */}
                                {votes_enabled === 1 && (
                                    <div>

                                        <div className="flex justify-between items-center mb-2">

                                            <span className="text-[10px] uppercase tracking-[2px] text-white/40">
                                                Vote Progress
                                            </span>

                                            <span className="text-xs font-bold text-white">
                                                {Math.round(percentage)}%
                                            </span>

                                        </div>

                                        <div className="relative h-3 rounded-full overflow-hidden bg-white/5 border border-white/5">

                                            {/* Glow Track */}
                                            <div className="absolute inset-0 bg-white/[0.02]" />

                                            {/* Fill */}
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold transition-all duration-700 shadow-[0_0_20px_rgba(255,180,0,0.5)]"
                                                style={{ width: `${percentage}%` }}
                                            />

                                        </div>

                                        <div className="flex justify-between mt-2 text-[10px] text-white/40">

                                            <span>
                                                {totalVotes.toLocaleString()} current
                                            </span>

                                            <span>
                                                {maxVotes.toLocaleString()} max
                                            </span>

                                        </div>

                                    </div>
                                )}

                                {/* BUTTON */}
                                {votes_enabled === 1 && (
                                    <Button
                                        onClick={() => handleVote(participant.id)}
                                        className="group/button relative overflow-hidden w-full h-12 rounded-2xl border-0 bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white font-black uppercase tracking-[2px] shadow-[0_10px_30px_rgba(255,180,0,0.25)] transition-all duration-300 hover:scale-[1.01]"
                                    >

                                        {/* Shine */}
                                        <div className="absolute inset-0 opacity-0 group-hover/button:opacity-100 transition duration-700">
                                            <div className="absolute -left-20 top-0 h-full w-16 bg-white/20 skew-x-12 blur-lg group-hover/button:left-[120%] transition-all duration-1000" />
                                        </div>

                                        <span className="relative z-10 flex items-center justify-center gap-2">
                                            <Heart className="w-4 h-4 fill-white" />
                                            Vote Now
                                        </span>

                                    </Button>
                                )}

                            </div>

                        </div>
                    );
                })}
            </div>


            {filteredParticipants.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-QWA2_TALENT_SHOW-gray text-lg">No participants found matching your search</p>
                </div>
            )}

            {/* PARTICIPANT DETAIL POPUP */}
            {selectedParticipant && (
                <ParticipantDetailPopup
                    participant={selectedParticipant}
                    totalVotes={selectedParticipant.votes + (userVotes[selectedParticipant.id] || 0)}
                    onClose={() => setSelectedParticipant(null)}
                    onPaymentSubmit={handlePaymentSubmit}
                    price={price}
                    eventid={parseInt(id || '1')}
                />
            )}
        </div>
    );
}

interface Countdown {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

function RegistrationDialog({ eventId }: { eventId?: string }) {
    const [open, setOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stageName, setStageName] = useState('');
    const [fullName, setFullName] = useState('');
    const [talent, setTalent] = useState('');
    const [isIndividual, setIsIndividual] = useState(true);
    const [description, setDescription] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [age, setAge] = useState('');
    const [location, setLocation] = useState('');



    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData();
        formData.append('event_id', eventId || '1');
        formData.append('stage_name', stageName);
        formData.append('full_name', fullName);
        formData.append('talent', talent);
        formData.append('is_individual', isIndividual ? '1' : '0');
        formData.append('description', description);
        if (image) formData.append('image', image);
        formData.append('age', age);
        formData.append('location', location);

        try {
            const res = await fetch(`${API_BASE}/api/v1/event/participants/register`, {
                method: 'POST',
                body: formData,
            });

            if (res.ok) {
                setSubmitted(true);
                setTimeout(() => {
                    setOpen(false);
                    setSubmitted(false);
                    // Reset form
                    setStageName('');
                    setFullName('');
                    setTalent('');
                    setIsIndividual(true);
                    setDescription('');
                    setImage(null);
                    setVideoUrl('');
                    setAge('');
                    setLocation('');
                }, 2000);
            } else {
                alert('Failed to submit registration');
            }
        } catch (error) {
            console.error('Error submitting:', error);
            alert('Error submitting registration');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-QWA2_TALENT_SHOW-red hover:bg-QWA2_TALENT_SHOW-red/90 text-white px-8 py-6 text-base font-bold uppercase tracking-wider rounded-2xl">
                    Register Now
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white border-2 border-QWA2_TALENT_SHOW-gold/20 shadow-2xl rounded-3xl">
                <DialogHeader className="text-center pb-6">
                    <div className="mx-auto w-16 h-16 bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold rounded-full flex items-center justify-center mb-4">
                        <Mic className="w-8 h-8 text-white" />
                    </div>
                    <DialogTitle className="text-3xl font-black text-QWA2_TALENT_SHOW-black bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold bg-clip-text text-transparent">
                        Join the Talent Show
                    </DialogTitle>
                    <DialogDescription className="text-base text-QWA2_TALENT_SHOW-gray mt-2">
                        Fill out the form below to register for the competition.
                    </DialogDescription>
                </DialogHeader>
                {submitted ? (
                    <div className="text-center py-16 space-y-4">
                        <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center">
                            <Check className="w-10 h-10 text-white" />
                        </div>
                        <p className="text-2xl font-black text-QWA2_TALENT_SHOW-black">Registration Submitted!</p>
                        <p className="text-lg text-QWA2_TALENT_SHOW-gray">We'll contact you soon with next steps.</p>
                        <div className="w-full bg-gradient-to-r from-QWA2_TALENT_SHOW-gold/20 to-QWA2_TALENT_SHOW-red/20 rounded-2xl p-4 mt-6">
                            <p className="text-sm text-QWA2_TALENT_SHOW-black">🎉 You're now part of the competition!</p>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid gap-6">
                            <label className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-QWA2_TALENT_SHOW-black">
                                    <User className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" />
                                    Stage Name *
                                </div>
                                <Input
                                    value={stageName}
                                    onChange={(e) => setStageName(e.target.value)}
                                    placeholder="Your stage name"
                                    required
                                    className="w-full rounded-2xl border-2 border-QWA2_TALENT_SHOW-gold/30 bg-white/90 px-4 py-3 text-sm outline-none focus:border-QWA2_TALENT_SHOW-gold focus:ring-2 focus:ring-QWA2_TALENT_SHOW-gold/20 transition-all"
                                />
                            </label>
                            <label className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-QWA2_TALENT_SHOW-black">
                                    <User className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" />
                                    Full Name
                                </div>
                                <Input
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder="Your full name"
                                    className="w-full rounded-2xl border-2 border-QWA2_TALENT_SHOW-gold/30 bg-white/90 px-4 py-3 text-sm outline-none focus:border-QWA2_TALENT_SHOW-gold focus:ring-2 focus:ring-QWA2_TALENT_SHOW-gold/20 transition-all"
                                />
                            </label>
                            <label className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-QWA2_TALENT_SHOW-black">
                                    <Mic className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" />
                                    Talent/Category *
                                </div>
                                <Input
                                    value={talent}
                                    onChange={(e) => setTalent(e.target.value)}
                                    placeholder="E.g. Singing, Dancing, Comedy"
                                    required
                                    className="w-full rounded-2xl border-2 border-QWA2_TALENT_SHOW-gold/30 bg-white/90 px-4 py-3 text-sm outline-none focus:border-QWA2_TALENT_SHOW-gold focus:ring-2 focus:ring-QWA2_TALENT_SHOW-gold/20 transition-all"
                                />
                            </label>
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-QWA2_TALENT_SHOW-black">
                                    <User className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" />
                                    Type
                                </div>
                                <div className="flex gap-6">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="type"
                                            checked={isIndividual}
                                            onChange={() => setIsIndividual(true)}
                                            className="w-4 h-4 text-QWA2_TALENT_SHOW-gold focus:ring-QWA2_TALENT_SHOW-gold"
                                        />
                                        <span className="text-sm text-QWA2_TALENT_SHOW-black">Individual</span>
                                    </label>
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="type"
                                            checked={!isIndividual}
                                            onChange={() => setIsIndividual(false)}
                                            className="w-4 h-4 text-QWA2_TALENT_SHOW-gold focus:ring-QWA2_TALENT_SHOW-gold"
                                        />
                                        <span className="text-sm text-QWA2_TALENT_SHOW-black">Group</span>
                                    </label>
                                </div>
                            </div>
                            <label className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-QWA2_TALENT_SHOW-black">
                                    <FileText className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" />
                                    Description *
                                </div>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Tell us about your talent and performance"
                                    className="w-full rounded-2xl border-2 border-QWA2_TALENT_SHOW-gold/30 bg-white/90 px-4 py-3 text-sm outline-none focus:border-QWA2_TALENT_SHOW-gold focus:ring-2 focus:ring-QWA2_TALENT_SHOW-gold/20 transition-all resize-none"
                                    rows={4}
                                    required
                                />
                            </label>
                            <label className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-QWA2_TALENT_SHOW-black">
                                    <ImageIcon className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" />
                                    Image
                                </div>
                                <div className="relative">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setImage(e.target.files?.[0] || null)}
                                        className="w-full rounded-2xl border-2 border-QWA2_TALENT_SHOW-gold/30 bg-white/90 px-4 py-3 text-sm outline-none focus:border-QWA2_TALENT_SHOW-gold focus:ring-2 focus:ring-QWA2_TALENT_SHOW-gold/20 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-QWA2_TALENT_SHOW-gold file:text-white hover:file:bg-QWA2_TALENT_SHOW-gold/90"
                                    />
                                </div>
                            </label>
                            <label className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-QWA2_TALENT_SHOW-black">
                                    <Calendar className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" />
                                    Age
                                </div>
                                <Input
                                    type="number"
                                    value={age}
                                    onChange={(e) => setAge(e.target.value)}
                                    placeholder="Your age"
                                    className="w-full rounded-2xl border-2 border-QWA2_TALENT_SHOW-gold/30 bg-white/90 px-4 py-3 text-sm outline-none focus:border-QWA2_TALENT_SHOW-gold focus:ring-2 focus:ring-QWA2_TALENT_SHOW-gold/20 transition-all"
                                />
                            </label>
                            <label className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-QWA2_TALENT_SHOW-black">
                                    <MapPin className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" />
                                    Location *
                                </div>
                                <Input
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="Your location"
                                    required
                                    className="w-full rounded-2xl border-2 border-QWA2_TALENT_SHOW-gold/30 bg-white/90 px-4 py-3 text-sm outline-none focus:border-QWA2_TALENT_SHOW-gold focus:ring-2 focus:ring-QWA2_TALENT_SHOW-gold/20 transition-all"
                                />
                            </label>
                        </div>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white font-bold uppercase tracking-wider py-4 rounded-2xl hover:opacity-90 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                        >
                            {isSubmitting && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            {isSubmitting ? "Registering..." : "Register Now"}
                        </Button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

function CompetitionsDetail() {
    const sectionRef = useRef<HTMLElement>(null);
    const [is_visible, setIsVisible] = useState(false);
    const id = useParams<{ id: string }>().id;
    const [registration_countdown, setRegistrationCountdown] = useState<Countdown>({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });
    const API_BASE = import.meta.env.VITE_API_BASE || "https://qwaqwatalentshow.co.za";
    const [start_countdown, setStartCountdown] = useState<Countdown>({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    const [participants, setParticipants] = useState<Participant[]>([]);
    const [totalParticipants, setTotalParticipants] = useState(0);
    const [loading, setLoading] = useState(true);
    const [event, setEvent] = useState<EventData | null>(null);



    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const eventId = id || '1';
                const res = await fetch(`${API_BASE}/api/v1/event?id=${eventId}`);
                if (res.ok) {
                    const data = await res.json();
                    setEvent(data.event);
                }
            } catch (error) {
                console.error('Failed to fetch event:', error);
            }
        };

        fetchEvent();
    }, [id]);


    const now = new Date(); // Current time

    // Define a default date string far in the future to ensure comparisons work correctly
    // when event data is not yet loaded or is missing date properties.
    // This makes `isStarted`, `isClosed`, `isRegistrationPassed` evaluate to `false`
    // until valid event dates are available and the current time passes them.
    const defaultFutureDateTime = '9999-12-31T23:59:59';

    // Combine date and time to create a full Date object, falling back to a future date if data is missing
    const startDate = new Date(event?.start_date && event?.start_time ? `${event.start_date}T${event.start_time}` : defaultFutureDateTime);
    const closeDate = new Date(event?.close_date && event?.close_time ? `${event.close_date}T${event.close_time}` : defaultFutureDateTime);
    // Assuming registrationDate is intended to be the same as closeDate for registration logic,
    // as there is no explicit 'registration_date' field in EventData.
    const registrationDate = new Date(event?.close_date && event?.close_time ? `${event.close_date}T${event.close_time}` : defaultFutureDateTime);

    // const isStarted = now >= startDate; // Check if current time is past the event start date
    const isClosed = now >= closeDate; // Check if current time is past the event close date
    const isRegistrationPassed = now >= registrationDate; // Check if current time is past the registration close date

    useEffect(() => {
        const calculateRegisterCountdown = () => {
            const targetDateStr = event ? `${event.close_date}T${event.close_time}` : '2026-05-01T23:59:59';
            const targetDate = new Date(targetDateStr).getTime();
            const now = new Date().getTime();
            const difference = targetDate - now;

            if (difference > 0) {
                setRegistrationCountdown({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60),
                });
            } else {
                setRegistrationCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            }
        };
        const calculateStartCountdown = () => {
            const targetDateStr = event ? `${event.start_date}T${event.start_time}` : '2026-05-01T23:59:59';
            const targetDate = new Date(targetDateStr).getTime();
            const now = new Date().getTime();
            const difference = targetDate - now;

            if (difference > 0) {
                setStartCountdown({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60),
                });
            } else {
                setStartCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            }
        };
        calculateRegisterCountdown();
        calculateStartCountdown();
        const interval = setInterval(() => {
            calculateRegisterCountdown();
            calculateStartCountdown();
        }, 1000);
        return () => clearInterval(interval);
    }, [event]);

    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                setLoading(true);

                const eventId = id || '1';

                const res = await fetch(
                    `${API_BASE}/api/v1/events/${eventId}/participants`,
                    {
                        credentials: 'include',
                    }
                );

                if (!res.ok) {
                    setParticipants([]);
                    setTotalParticipants(0);
                    return;
                }

                const data = await res.json();

                console.log(data.participants);

                // ✅ Ensure it's always an array
                const participantsArray = Array.isArray(data.participants)
                    ? data.participants
                    : [];

                setTotalParticipants(data.participants?.length || 0);

                const fetchedParticipants: Participant[] = participantsArray.map((p: any) => ({
                    ...p,
                    competition: p.talent || 'Performance',
                    image: p.image || success_1,
                    is_individual: Boolean(p.is_individual),
                }));

                setParticipants(fetchedParticipants);

            } catch (error) {
                console.error('Failed to fetch participants:', error);
                setParticipants([]);
            } finally {
                setLoading(false);
            }
        };

        fetchParticipants();
    }, [id]);



    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 }
        );

        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="min-h-screen bg-white">
            <Navigation />
            <main>
                <section className="relative min-h-[60vh] overflow-hidden bg-black text-white">
                    <div className="absolute inset-0">
                        <img src={event?.primary_image || highlight_1} alt="Competition hero" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/65" />
                    </div>

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 mt-[60px] min-h-screen flex flex-col justify-center">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div className="max-w-2xl">
                                <div className="flex flex-wrap gap-3 mb-6">
                                    <span className="inline-flex rounded-full bg-QWA2_TALENT_SHOW-gold/20 px-4 py-2 text-xs uppercase tracking-[0.35em] text-QWA2_TALENT_SHOW-gold">
                                        {event?.category || 'Competition'} Details
                                    </span>
                                    {event?.status && (
                                        <div className="flex flex-wrap items-center gap-3">
                                            {event.registration_enabled === 1 && isClosed ? (
                                                <span className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-black uppercase tracking-[0.2em]">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]" />
                                                    Registration Closed
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-QWA2_TALENT_SHOW-gold/10 border border-QWA2_TALENT_SHOW-gold/20 text-QWA2_TALENT_SHOW-gold text-[10px] font-black uppercase tracking-[0.2em]">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-QWA2_TALENT_SHOW-gold shadow-[0_0_8px_rgba(212,175,55,0.8)]" />
                                                    Upcoming
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <h1 className="mt-6 text-4xl md:text-5xl font-black leading-tight">
                                    {event ? event.title : ''}
                                </h1>
                                <p className="mt-5 text-base md:text-lg text-white/80">
                                    {event ? event.description : '.'}
                                </p>
                            </div>

                            <div className="space-y-6">
                                {event?.registration_enabled === 1 && !isRegistrationPassed && (
                                    <div className="rounded-3xl bg-white/5 border border-white/10 p-6 md:p-8 backdrop-blur-xl shadow-2xl relative overflow-hidden group">
                                        {/* Subtle ambient light effect */}
                                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-QWA2_TALENT_SHOW-gold/10 blur-[100px] rounded-full transition-colors duration-700 group-hover:bg-QWA2_TALENT_SHOW-gold/20" />

                                        <div className="relative z-10">
                                            {/* Registration Countdown Section */}
                                            {event?.registration_enabled === 1 && !isRegistrationPassed && (
                                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-1000">
                                                    <div className="flex items-center gap-4">
                                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                                                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-white/40">
                                                            Registration Closes In
                                                        </p>
                                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                                                    </div>

                                                    <div className="grid grid-cols-4 gap-3 sm:gap-6">
                                                        {[
                                                            { label: "Days", value: registration_countdown?.days, color: "text-white" },
                                                            { label: "Hours", value: registration_countdown?.hours, color: "text-white" },
                                                            { label: "Mins", value: registration_countdown?.minutes, color: "text-white" },
                                                            { label: "Secs", value: registration_countdown?.seconds, color: "text-QWA2_TALENT_SHOW-red" }
                                                        ].map((item, index) => (
                                                            <div key={index} className="flex flex-col items-center">
                                                                <div className="w-full py-4 flex flex-col items-center justify-center bg-white/[0.03] border border-white/10 rounded-2xl shadow-xl backdrop-blur-md transition-all duration-300 hover:bg-white/[0.06] hover:border-white/20">
                                                                    <span className={`text-2xl sm:text-4xl font-black tabular-nums tracking-tighter ${item.color}`}>
                                                                        {String(item.value ?? 0).padStart(2, "0")}
                                                                    </span>
                                                                    <span className="text-[8px] sm:text-[10px] uppercase font-bold tracking-[0.2em] text-white/30 mt-1">
                                                                        {item.label}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="rounded-3xl bg-white/5 border border-white/10 p-6 md:p-8 backdrop-blur-xl shadow-2xl relative overflow-hidden group">
                                    <div className="absolute -top-24 -right-24 w-48 h-48 bg-QWA2_TALENT_SHOW-gold/10 blur-[100px] rounded-full transition-colors duration-700 group-hover:bg-QWA2_TALENT_SHOW-gold/20" />

                                    <div className="relative z-10 text-center space-y-3">
                                        <p className="text-sm uppercase tracking-[0.3em] text-white/40 font-bold">
                                            {startDate.getTime() > now.getTime()
                                                ? "Event Starts"
                                                : "Event Started"}
                                        </p>

                                        <h2 className="text-2xl sm:text-4xl font-black text-white">
                                            {startDate.toLocaleDateString("en-ZA", {
                                                weekday: "long",
                                                day: "numeric",
                                                month: "long",
                                                year: "numeric",
                                            })}
                                        </h2>

                                        <p className="text-lg sm:text-xl text-QWA2_TALENT_SHOW-gold font-semibold">
                                            {startDate.toLocaleTimeString("en-ZA", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="relative py-20 md:py-28 bg-white overflow-hidden">
                    <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />
                    <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-red/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            className={`grid gap-6 ${event?.votes_enabled === 1
                                    ? 'md:grid-cols-2 xl:grid-cols-4'
                                    : 'md:grid-cols-2 xl:grid-cols-3'
                                }`}
                        >

                            {/* VENUE */}
                            <div className="group relative overflow-hidden rounded-[28px] border border-gray-200/70 bg-white p-6 shadow-[0_10px_40px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]">

                                {/* Glow */}
                                <div className="absolute top-0 right-0 w-28 h-28 bg-QWA2_TALENT_SHOW-gold/10 blur-3xl rounded-full" />

                                <div className="relative z-10">

                                    <div className="flex items-center justify-between mb-6">

                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-QWA2_TALENT_SHOW-gold/10 to-QWA2_TALENT_SHOW-gold/20 flex items-center justify-center border border-QWA2_TALENT_SHOW-gold/10">
                                            <MapPin className="w-6 h-6 text-QWA2_TALENT_SHOW-gold" />
                                        </div>

                                        <span className="text-[10px] uppercase tracking-[3px] text-gray-400 font-bold">
                                            Venue
                                        </span>

                                    </div>

                                    <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-black leading-tight line-clamp-2">
                                        {event?.details?.venue_details || 'Nothing to show'}
                                    </h3>

                                    <p className="mt-3 text-sm text-gray-500">
                                        Event location details
                                    </p>

                                </div>

                            </div>

                            {/* ENTRY FEE */}
                            <div className="group relative overflow-hidden rounded-[28px] border border-gray-200/70 bg-white p-6 shadow-[0_10px_40px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]">

                                <div className="absolute bottom-0 left-0 w-28 h-28 bg-QWA2_TALENT_SHOW-red/10 blur-3xl rounded-full" />

                                <div className="relative z-10">

                                    <div className="flex items-center justify-between mb-6">

                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-QWA2_TALENT_SHOW-red/10 to-QWA2_TALENT_SHOW-red/20 flex items-center justify-center border border-QWA2_TALENT_SHOW-red/10">
                                            <Ticket className="w-6 h-6 text-QWA2_TALENT_SHOW-red" />
                                        </div>

                                        <span className="text-[10px] uppercase tracking-[3px] text-gray-400 font-bold">
                                            Entry
                                        </span>

                                    </div>

                                    <h3 className="text-3xl font-black text-QWA2_TALENT_SHOW-black">
                                        {event?.details?.entry_fee || 'FREE'}
                                    </h3>

                                    <p className="mt-3 text-sm text-gray-500">
                                        Competition registration fee
                                    </p>

                                </div>

                            </div>

                            {/* AGE */}
                            <div className="group relative overflow-hidden rounded-[28px] border border-gray-200/70 bg-white p-6 shadow-[0_10px_40px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]">

                                <div className="absolute top-0 left-0 w-28 h-28 bg-blue-500/10 blur-3xl rounded-full" />

                                <div className="relative z-10">

                                    <div className="flex items-center justify-between mb-6">

                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-500/20 flex items-center justify-center border border-blue-500/10">
                                            <Info className="w-6 h-6 text-blue-500" />
                                        </div>

                                        <span className="text-[10px] uppercase tracking-[3px] text-gray-400 font-bold">
                                            Age
                                        </span>

                                    </div>

                                    <h3 className="text-3xl font-black text-QWA2_TALENT_SHOW-black">
                                        {event?.details?.age_requirement || 'All Ages'}
                                    </h3>

                                    <p className="mt-3 text-sm text-gray-500">
                                        Participant requirement
                                    </p>

                                </div>

                            </div>

                            {/* VOTE CARD */}
                            {event?.votes_enabled === 1 && (
                                <div className="group relative overflow-hidden rounded-[28px] bg-gradient-to-br from-QWA2_TALENT_SHOW-gold via-[#f7b733] to-QWA2_TALENT_SHOW-red p-[1px] shadow-[0_15px_50px_rgba(255,180,0,0.25)] transition-all duration-300 hover:-translate-y-1">

                                    <div className="relative h-full rounded-[27px] bg-white p-6 overflow-hidden">

                                        {/* Glow */}
                                        <div className="absolute -top-10 right-0 w-36 h-36 bg-QWA2_TALENT_SHOW-gold/20 blur-3xl rounded-full" />

                                        <div className="relative z-10">

                                            <div className="flex items-center justify-between mb-6">

                                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold flex items-center justify-center shadow-lg">
                                                    <Heart className="w-6 h-6 text-white fill-white" />
                                                </div>

                                                <div className="px-3 py-1 rounded-full bg-green-100 text-green-600 text-[10px] uppercase tracking-[2px] font-black">
                                                    Live Voting
                                                </div>

                                            </div>

                                            <p className="text-[10px] uppercase tracking-[3px] text-gray-400 font-bold mb-2">
                                                Vote Price
                                            </p>

                                            <h3 className="text-4xl font-black bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold bg-clip-text text-transparent">
                                                R{event?.details?.price_per_vote || 0}
                                            </h3>

                                            <p className="mt-3 text-sm text-gray-500">
                                                Support your favorite contestant
                                            </p>

                                        </div>

                                    </div>

                                </div>
                            )}

                        </div>

                        {/* Dynamic Registration & Live Stream Row */}
                        <div className="mt-8 grid gap-8 md:grid-cols-2">
                            <div className="rounded-3xl bg-white p-8 border-2 border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="text-center md:text-left">
                                    <h3 className="text-2xl font-black text-gray-900">Competition Registration</h3>
                                    <p className="text-gray-500 mt-1">
                                        {event?.registration_enabled === 1
                                            ? 'Slots are currently available for this category.'
                                            : 'Registration is currently closed for this event.'}
                                    </p>
                                </div>

                                {event?.registration_enabled === 1 && !isRegistrationPassed ? (
                                    <div className="flex-shrink-0">
                                        <RegistrationDialog eventId={id} />
                                    </div>
                                ) : null}
                            </div>

                            <div className="rounded-3xl bg-black p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-QWA2_TALENT_SHOW-red/20 blur-3xl" />
                                <div className="relative z-10 text-center md:text-left">
                                    <h3 className="text-2xl font-black">Event Live Stream</h3>
                                    <p className="text-white/60 mt-1">
                                        {event?.youtube_live_id ? 'Broadcasting live now on YouTube.' : 'No live stream scheduled.'}
                                    </p>
                                </div>
                                {event?.youtube_live_id && (
                                    <a
                                        href={event.youtube_live_url || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="relative z-10 flex items-center gap-2 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-xl font-bold transition-all animate-pulse"
                                    >
                                        <Video className="w-5 h-5" />
                                        Watch Live
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* YouTube Embed Section (Only if live id exists) */}
                        {event?.youtube_live_id && (
                            <div className="mt-8 rounded-3xl overflow-hidden shadow-2xl bg-black aspect-video border-4 border-QWA2_TALENT_SHOW-gold/20">
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src={`https://www.youtube.com/embed/${event.youtube_live_id}`}
                                    title="YouTube video player"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        )}

                        <div className="mt-16 rounded-3xl bg-gradient-to-r from-black/5 to-QWA2_TALENT_SHOW-gold/5 border border-QWA2_TALENT_SHOW-gold/20 p-8 md:p-12">
                            <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black mb-6">Why Join Our Competitions?</h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-QWA2_TALENT_SHOW-gold/20 flex items-center justify-center">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">✓</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-QWA2_TALENT_SHOW-black">Showcase Your Talent</h3>
                                            <p className="text-sm text-QWA2_TALENT_SHOW-gray mt-1">Display your skills on a prestigious platform and gain recognition</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-QWA2_TALENT_SHOW-gold/20 flex items-center justify-center">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">✓</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-QWA2_TALENT_SHOW-black">Network with Professionals</h3>
                                            <p className="text-sm text-QWA2_TALENT_SHOW-gray mt-1">Connect with industry leaders and fellow competitors</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-QWA2_TALENT_SHOW-gold/20 flex items-center justify-center">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">✓</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-QWA2_TALENT_SHOW-black">Win Amazing Prizes</h3>
                                            <p className="text-sm text-QWA2_TALENT_SHOW-gray mt-1">Compete for cash prizes, sponsorships, and exclusive opportunities</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-QWA2_TALENT_SHOW-gold/20 flex items-center justify-center">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">✓</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-QWA2_TALENT_SHOW-black">Build Your Portfolio</h3>
                                            <p className="text-sm text-QWA2_TALENT_SHOW-gray mt-1">Add credibility and accomplishments to your professional profile</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-QWA2_TALENT_SHOW-gold/20 flex items-center justify-center">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">✓</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-QWA2_TALENT_SHOW-black">Get Exposure & Media Coverage</h3>
                                            <p className="text-sm text-QWA2_TALENT_SHOW-gray mt-1">Be featured in news outlets, social media, and press releases</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-QWA2_TALENT_SHOW-gold/20 flex items-center justify-center">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">✓</span>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-QWA2_TALENT_SHOW-black">Growing Community</h3>
                                            <p className="text-sm text-QWA2_TALENT_SHOW-gray mt-1">Join thousands of talented individuals worldwide</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>



                <section className="relative pb-20 bg-white overflow-hidden">
                    <div className="absolute top-10 right-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />
                    <div className="absolute bottom-10 left-10 w-32 h-32 rounded-full bg-QWA2_TALENT_SHOW-red/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <ParticipantsSection id={id} participants={participants} totalParticipants={totalParticipants} loading={loading} price={event?.details?.price_per_vote || 0} votes_enabled={event?.votes_enabled || 0} />
                    </div>
                </section>



                <FooterSection />
            </main>
        </div>
    );
}

export default CompetitionsDetail;
